import { SurveyRuleActionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleActionValidationError";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";
import { TFunction } from "i18next";
import { SurveyRuleCommonValidationError } from "../../utils/getSurveyRuleValidationErrorValues";

type ConditionValidationError =
  | SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
  | SurveyRuleCommonValidationError;

type ActionValidationError =
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError
  | SurveyRuleCommonValidationError;

export const getConditionKeyValidationErrorHelperText = (
  t: TFunction,
  validationErrors: readonly ConditionValidationError[],
): string | undefined => {
  const validationError = validationErrors.at(0);

  switch (validationError) {
    case undefined:
      return undefined;
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_KEY:
      return t("surveys.rules.validationErrors.invalidQuestionKey");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_RESPONDENT_KEY:
      return t("surveys.rules.validationErrors.invalidRespondentKey");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_TYPE:
      return t("surveys.rules.validationErrors.invalidQuestionType");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_KEY_GROUP_CHILD:
      return t("surveys.rules.validationErrors.invalidQuestionKeyGroupChild");
    case SurveyRuleCommonValidationError.EMPTY_VALUE:
      return t("surveys.rules.validationErrors.emptySourceKey");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_OPTION_CODE_VALUE:
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .EXACT_ONE_CONDITION_MUST_HAVE_NONE_CONNECTOR:
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_RESPONDENT_VALUE:
      return undefined;
    default:
      validationError satisfies never;
      return undefined;
  }
};

export const getConditionValueValidationErrorHelperText = (
  t: TFunction,
  validationErrors: readonly ConditionValidationError[],
): string | undefined => {
  const validationError = validationErrors.at(0);

  switch (validationError) {
    case undefined:
      return undefined;
    case SurveyRuleCommonValidationError.EMPTY_VALUE:
      return t("surveys.rules.validationErrors.emptySourceValue");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_OPTION_CODE_VALUE:
      return t("surveys.rules.validationErrors.invalidOptionCodeValue");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_RESPONDENT_VALUE:
      return t("surveys.rules.validationErrors.invalidRespondentValue");
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_KEY:
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_TYPE:
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_KEY_GROUP_CHILD:
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_RESPONDENT_KEY:
    case SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .EXACT_ONE_CONDITION_MUST_HAVE_NONE_CONNECTOR:
      return undefined;
    default:
      validationError satisfies never;
      return undefined;
  }
};

export const getActionValidationErrorHelperText = (
  t: TFunction,
  validationErrors: readonly ActionValidationError[],
): string | undefined => {
  const validationError = validationErrors.at(0);

  switch (validationError) {
    case undefined:
      return undefined;
    case SurveyRuleCommonValidationError.EMPTY_VALUE:
      return t("surveys.rules.validationErrors.emptyAction");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_MULTI_CHOICE:
      return t("surveys.rules.validationErrors.invalidActionMultiChoice");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_RESPONDENT:
      return t("surveys.rules.validationErrors.invalidActionRespondent");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_KEY_TARGET_ORDER:
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_GROUP_CHILD:
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_GRID_ROW:
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_SKIP_TO_TARGET_COUNT:
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_TARGET:
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_TARGET_GRID_ROW:
      return undefined;
    default:
      validationError satisfies never;
      return undefined;
  }
};

export const getTargetValidationErrorHelperText = (
  t: TFunction,
  validationErrors: readonly ActionValidationError[],
): string | undefined => {
  const validationError = validationErrors.at(0);

  switch (validationError) {
    case undefined:
      return undefined;
    case SurveyRuleCommonValidationError.EMPTY_VALUE:
      return t("surveys.rules.validationErrors.emptyTarget");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_SKIP_TO_TARGET_COUNT:
      return t("surveys.rules.validationErrors.invalidSkipToTargetCount");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_TARGET:
      return t("surveys.rules.validationErrors.invalidTarget");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_GROUP_CHILD:
      return t("surveys.rules.validationErrors.invalidActionGroupChild");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_TARGET_GRID_ROW:
      return t("surveys.rules.validationErrors.invalidTargetGridRow");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_GRID_ROW:
      return t("surveys.rules.validationErrors.invalidActionGridRow");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_KEY_TARGET_ORDER:
      return t("surveys.rules.validationErrors.invalidKeyTargetOrder");
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_MULTI_CHOICE:
    case SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_RESPONDENT:
      return undefined;
    default:
      validationError satisfies never;
      return undefined;
  }
};
